// Runs candidate JavaScript in an opaque-origin sandboxed iframe (no cookies, storage or
// access to the app) whose CSP blocks all network, inside a Worker so runaway loops can be
// killed. The code becomes the worker's script source; nothing is eval'd in the app.
// Logs stream out line by line, so a timeout still returns everything printed so far.
const MAX_LINES = 1000;
const MAX_CHARS = 20000;

const PRELUDE = [
  'var __n = 0, __size = 0, __cut = false, __pending = 0, __main = false, __done = false, __live = {};',
  'var __post = self.postMessage.bind(self);',
  'var __fmt = function (x) { if (x instanceof Error) return x.stack ? String(x.stack) : String(x);',
  '  try { return typeof x === "object" && x !== null ? JSON.stringify(x) : String(x); } catch (_) { return String(x); } };',
  'var __cap = function () { if (__done || __cut) return; var line = [].map.call(arguments, __fmt).join(" ");',
  `  __n++; __size += line.length; if (__n > ${MAX_LINES} || __size > ${MAX_CHARS}) { __cut = true; line = "... output truncated"; }`,
  '  __post({ log: line }); };',
  'self.console = { log: __cap, info: __cap, warn: __cap, error: __cap, debug: __cap };',
  'var __end = function (stderr) { if (__done) return; __done = true; __post({ done: true, stderr: stderr || "" }); };',
  'self.onerror = function (msg, src, line, col, err) { __end(err ? __fmt(err) : String(msg)); return true; };',
  'self.onunhandledrejection = function (e) { e.preventDefault(); __end("Uncaught (in promise) " + __fmt(e.reason)); };',
  // Finish only once the main body has resolved AND no timers/intervals are still pending.
  'var __st = self.setTimeout, __ct = self.clearTimeout, __si = self.setInterval, __ci = self.clearInterval;',
  // 20ms grace lets the browser deliver any pending unhandledrejection event before we finish.
  'var __settle = function () { __st(function () { if (__main && __pending === 0) __end(""); }, 20); };',
  'var __drop = function (id) { if (__live[id]) { delete __live[id]; __pending--; } };',
  'self.setTimeout = function (fn, ms) { var a = [].slice.call(arguments, 2); var id = __st(function () {',
  '  __drop(id); try { if (typeof fn === "function") fn.apply(null, a); } finally { __settle(); } }, ms);',
  '  __live[id] = 1; __pending++; return id; };',
  'self.setInterval = function () { var id = __si.apply(null, arguments); __live[id] = 1; __pending++; return id; };',
  'self.clearTimeout = function (id) { __drop(id); __ct(id); __settle(); };',
  'self.clearInterval = function (id) { __drop(id); __ci(id); __settle(); };',
  // Async wrapper so top-level `await` works and sync throws surface as errors.
  ';(async function () { "use strict";',
  '',
].join('\n');
const EPILOGUE = '\n})().then(function () { __main = true; __settle(); }, function (e) { __end(__fmt(e)); });\n';
const CODE_LINE_OFFSET = PRELUDE.split('\n').length - 1;

// Map worker stack frames (blob:null/<uuid>:L:C) to the candidate's own line numbers; drop runner frames.
const cleanStack = (stderr, codeLines) => stderr.split('\n').reduce((out, line) => {
  const m = line.match(/blob:null\/[\w-]+:(\d+):(\d+)/);
  if (!m) return [...out, line];
  const codeLine = Number(m[1]) - CODE_LINE_OFFSET;
  return codeLine > 0 && codeLine <= codeLines ? [...out, line.replace(m[0], `solution.js:${codeLine}:${m[2]}`)] : out;
}, []).join('\n');
const CSP = "default-src 'none'; script-src 'unsafe-inline' blob:; worker-src blob:; connect-src 'none'";

// The candidate's code is embedded in the sandbox page itself ("<" escaped so it can't close the
// script tag), so nothing is ever posted *into* the iframe; results go back to this exact origin only.
const runnerHtml = (code, origin) => `<!doctype html><html><head><meta http-equiv="Content-Security-Policy" content="${CSP}"></head><body><script>
var PRELUDE = ${JSON.stringify(PRELUDE)}, EPILOGUE = ${JSON.stringify(EPILOGUE)};
var CODE = ${JSON.stringify(String(code)).replace(/</g, '\\u003c')}, TARGET = ${JSON.stringify(origin)};
var send = function (msg) { parent.postMessage(msg, TARGET); };
var w = new Worker(URL.createObjectURL(new Blob([PRELUDE, CODE, EPILOGUE], { type: 'text/javascript' })));
w.onmessage = function (m) { var d = m.data || {};
  if (typeof d.log === 'string') send({ log: d.log });
  else if (d.done) send({ result: true, stderr: String(d.stderr || '') }); };
w.onerror = function (ev) { ev.preventDefault(); send({ result: true, stderr: ev.message || 'Runtime error' }); };
</script></body></html>`;

export function runJsSandboxed(code, timeoutMs = 5000) {
  return new Promise((resolve) => {
    const frame = document.createElement('iframe');
    frame.setAttribute('sandbox', 'allow-scripts');
    frame.setAttribute('aria-hidden', 'true');
    frame.setAttribute('data-testid', 'js-sandbox-frame');
    frame.style.display = 'none';
    const lines = [];
    const started = Date.now();
    let timer;
    const finish = (stderr, timedOut) => {
      clearTimeout(timer);
      window.removeEventListener('message', onMessage);
      frame.remove();
      resolve({ stdout: lines.join('\n'), stderr, timed_out: timedOut, duration_ms: Date.now() - started });
    };
    const onMessage = (e) => {
      if (e.source !== frame.contentWindow || e.origin !== 'null' || !e.data) return;
      if (typeof e.data.log === 'string') {
        if (lines.length <= MAX_LINES) lines.push(e.data.log.slice(0, MAX_CHARS));
      } else if (e.data.result) {
        finish(cleanStack(String(e.data.stderr || ''), String(code).split('\n').length), false);
      }
    };
    timer = setTimeout(() => finish(
      `Execution timed out (${timeoutMs / 1000}s limit). Output printed so far is shown above. `
      + 'Check for infinite loops, intervals that are never cleared, or promises that never settle.',
      true,
    ), timeoutMs);
    window.addEventListener('message', onMessage);
    frame.srcdoc = runnerHtml(code, window.location.origin);
    document.body.appendChild(frame);
  });
}
